import React, { useState, useEffect, lazy, Suspense, startTransition } from "react";
import { HelmetProvider } from "react-helmet-async";
import Preloader from "../src/components/Pre";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Particle from "./components/Particle";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import "./style.css";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";

// Import Home2 directly (most visited page - should be in main bundle)
import Home2 from "./components/Home/Home2";

// Lazy load other pages
const Services = lazy(() => import("./components/Services/Services"));
const Projects = lazy(() => import("./components/Projects/Projects"));
const Process = lazy(() => import("./components/Process/Process"));
const Contact = lazy(() => import("./components/Contact/Contact"));
const Admin = lazy(() => import("./components/Admin/Admin"));

// Loading fallback for lazy pages
const PageLoader = () => (
  <div style={{ 
    minHeight: '60vh', 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center' 
  }}>
    <div className="page-loading-spinner" />
  </div>
);

// Keep pages mounted for instant navigation
function PersistentRoutes() {
  const location = useLocation();
  const [visitedRoutes, setVisitedRoutes] = useState(new Set(['/']));

  useEffect(() => {
    // Track visited routes to keep them mounted
    startTransition(() => {
      setVisitedRoutes(prev => new Set([...prev, location.pathname]));
    });
  }, [location.pathname]);

  return (
    <>
      {/* Home - always mounted, just hidden when not active */}
      <div style={{ display: location.pathname === '/' ? 'block' : 'none' }}>
        <Home2 />
      </div>

      {/* Other pages - mounted once visited, then kept in DOM */}
      {visitedRoutes.has('/services') && (
        <div style={{ display: location.pathname === '/services' ? 'block' : 'none' }}>
          <Suspense fallback={<PageLoader />}>
            <Services />
          </Suspense>
        </div>
      )}

      {visitedRoutes.has('/work') && (
        <div style={{ display: location.pathname === '/work' ? 'block' : 'none' }}>
          <Suspense fallback={<PageLoader />}>
            <Projects />
          </Suspense>
        </div>
      )}

      {visitedRoutes.has('/process') && (
        <div style={{ display: location.pathname === '/process' ? 'block' : 'none' }}>
          <Suspense fallback={<PageLoader />}>
            <Process />
          </Suspense>
        </div>
      )}

      {visitedRoutes.has('/contact') && (
        <div style={{ display: location.pathname === '/contact' ? 'block' : 'none' }}>
          <Suspense fallback={<PageLoader />}>
            <Contact />
          </Suspense>
        </div>
      )}

      {visitedRoutes.has('/admin') && (
        <div style={{ display: location.pathname === '/admin' ? 'block' : 'none' }}>
          <Suspense fallback={<PageLoader />}>
            <Admin />
          </Suspense>
        </div>
      )}

      {/* Redirect unknown routes */}
      <Routes>
        <Route path="/" element={null} />
        <Route path="/services" element={null} />
        <Route path="/work" element={null} />
        <Route path="/process" element={null} />
        <Route path="/contact" element={null} />
        <Route path="/admin" element={null} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  const [load, upadateLoad] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      upadateLoad(false);
    }, 1200);

    return () => clearTimeout(timer);
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <Preloader load={load} />
        {/* Global particle effect - covers entire page */}
        <Particle />
        <div className="App" id={load ? "no-scroll" : "scroll"}>
          <Navbar />
          <ScrollToTop />
          <PersistentRoutes />
          <Footer />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;
